import { QRCodeSVG } from 'qrcode.react';
import { useState, type FormEvent } from 'react';
import type { TwoFactorSetupResponse } from '@/shared/api';
import { Button, FormField, Input } from '@/shared/components';

interface Props {
  setup: TwoFactorSetupResponse;
  /** Fehler des letzten Bestätigens als Satz; null ohne Fehler. */
  failure?: string | null;
  onCancel: () => void;
  onConfirm: (code: string) => Promise<void>;
}

/**
 * Zwei-Faktor einrichten, Schritt „Verbinden“: QR-Code aus der Adresse des Servers, darunter der
 * Schlüssel zum Abtippen oder Kopieren, dann der erste Code aus der App.
 */
export function TwoFactorSetupPanel({ setup, failure = null, onCancel, onConfirm }: Props) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState<'ok' | 'failed' | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    try {
      await onConfirm(code);
    } finally {
      setSubmitting(false);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(setup.sharedKey.replace(/\s/g, ''));
      setCopied('ok');
    } catch {
      setCopied('failed');
    }
  };

  return (
    <section className="border-hairline rounded-tile @container flex flex-col gap-4 border bg-white p-5">
      <div className="@[520px]:flex-row @[520px]:items-start flex flex-col gap-4">
        <div
          role="img"
          aria-label="QR-Code für die Authenticator-App"
          className="border-hairline rounded-control @[520px]:self-start w-44 flex-none self-center border bg-white p-3"
        >
          <QRCodeSVG
            value={setup.authenticatorUri}
            size={152}
            level="M"
            className="h-auto w-full"
          />
        </div>
        <div className="flex min-w-0 flex-col gap-2">
          <b className="font-serif text-xl">QR-Code scannen</b>
          <p className="text-ink-soft text-sm leading-relaxed">
            Scann den Code mit deiner Authenticator-App. Geht das nicht, tipp den Schlüssel ab.
          </p>
          <div className="bg-card-alt rounded-control flex flex-wrap items-center gap-2 px-3 py-2">
            <code className="min-w-0 flex-1 select-all break-all font-mono text-sm">
              {setup.sharedKey}
            </code>
            <Button
              type="button"
              variant="secondary"
              className="min-h-11"
              onClick={() => void copy()}
            >
              {copied === 'ok' ? 'Kopiert' : 'Kopieren'}
            </Button>
          </div>
          {copied === 'failed' && (
            <p className="text-ink-soft text-xs">Kopieren ging nicht – markier den Schlüssel.</p>
          )}
        </div>
      </div>
      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-3" noValidate>
        {failure && (
          <p
            role="alert"
            className="bg-sealwax-tint text-sealwax rounded-control p-3 text-sm font-semibold"
          >
            {failure}
          </p>
        )}
        <FormField
          label="Code aus der App"
          hint="Die sechs Ziffern, die die App gerade zeigt."
          required
        >
          <Input
            value={code}
            onChange={(event) => setCode(event.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            className="font-mono tracking-[0.2em]"
            autoFocus
          />
        </FormField>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" size="lg" onClick={onCancel}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant="brand"
            size="lg"
            loading={submitting}
            disabled={!code.trim()}
          >
            Verbinden
          </Button>
        </div>
      </form>
    </section>
  );
}
